import { Reorder } from 'motion/react';
import React, { useState } from 'react';
import Card from './Card';
import * as styles from './ReorderableList.module.scss';
import ReorderableListRow from './ReorderableListRow';

type BasicReorderableItem = { id: string };

const getIdToIndexMap = <T extends BasicReorderableItem>(
  items: T[],
): Map<string, number> => new Map(items.map((item, index) => [item.id, index]));

interface ReorderableListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => JSX.Element;
  isRemovable?: (item: T, index: number) => boolean;
  onChange: (items: T[]) => void;
}

const ReorderableList = <T extends BasicReorderableItem>({
  items,
  renderItem,
  isRemovable,
  onChange,
}: ReorderableListProps<T>) => {
  const [indexBeforeDraggingMap, setIndexBeforeDraggingMap] = useState<Map<
    string,
    number
  > | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <Card noPadding>
      <Reorder.Group axis="y" values={items} onReorder={onChange} as="div">
        <div className={styles.orderContainer}>
          {items.map((item, index) => (
            <Reorder.Item
              key={item.id}
              value={item}
              as="div"
              className={styles.draggableContainer}
              transition={
                draggingId == null ? { duration: 0 } : { duration: 0.2 }
              }
              onDragStart={() => {
                setIndexBeforeDraggingMap(getIdToIndexMap(items));
                setDraggingId(item.id);
              }}
              onDragEnd={() => {
                setIndexBeforeDraggingMap(null);
                setDraggingId(null);
              }}
            >
              <ReorderableListRow
                item={item}
                index={index}
                indexBeforeDragging={
                  indexBeforeDraggingMap?.get(item.id) ?? index
                }
                isDragging={draggingId === item.id}
                onRemove={
                  isRemovable?.(item, index)
                    ? () => onChange(items.filter((_, i) => i !== index))
                    : undefined
                }
                renderItem={renderItem}
              />
            </Reorder.Item>
          ))}
        </div>
      </Reorder.Group>
    </Card>
  );
};

export default ReorderableList;
